import { ReactNode, useContext, useEffect } from "react";
import { Trans } from "react-i18next";

import { classNames, uuid } from "../../utils";
import { Code } from "../ui/Code";
import { FormInputGroupContext } from "./FormInputGroup";

type Props = {
  children: ReactNode;
  className?: string;
  error?: string; // eslint-disable-line upscope/no-error-string
  explanation?: ReactNode | string;
  hiddenLabel?: boolean;
  label: string;
  testId?: string;
};

export default function FormInput({ children, className, error, explanation, hiddenLabel, label, testId }: Props) {
  hiddenLabel ??= false;

  const { addGroupError, addGroupExplanation, inGroup, removeGroupError, removeGroupExplanation } =
    useContext(FormInputGroupContext);

  useEffect(() => {
    if (!inGroup) return;
    if (!error) return;
    const id = uuid();
    addGroupError(id, error);
    return () => {
      removeGroupError(id);
    };
  }, [inGroup, error, addGroupError, removeGroupError]);

  useEffect(() => {
    if (!inGroup) return;
    if (error) return;
    if (typeof explanation === "undefined") return;
    const id = uuid();
    addGroupExplanation(id, explanation);
    return () => {
      removeGroupExplanation(id);
    };
  }, [inGroup, error, explanation, addGroupExplanation, removeGroupExplanation]);

  return (
    <label
      className={classNames("flex flex-col w-full mb-6 space-y-1.5", error && "group has-error", className)}
      data-testid={testId}
    >
      <div
        className={classNames(
          "text-sm font-semibold text-gray-600 dark:text-gray-300",
          hiddenLabel && "hidden sm:block invisible",
        )}
      >
        {label}
      </div>
      {children}
      {!inGroup && typeof explanation !== "undefined" && !error && <ExplanationLabel>{explanation}</ExplanationLabel>}
      {!inGroup && error && <ErrorLabel>{error}</ErrorLabel>}
    </label>
  );
}

export const ExplanationLabel = ({ children }: { children: React.ReactNode }) => {
  return (
    <p className="underlined-links text-sm text-gray-400 dark:text-gray-500">
      {typeof children === "string" ? <Trans components={{ code: <Code /> }}>{children}</Trans> : children}
    </p>
  );
};

export const ErrorLabel = ({ children }: { children: React.ReactNode }) => {
  return (
    <p className="text-sm text-red-500" data-role="error_message">
      {children}
    </p>
  );
};

export function NoLabelFormInput({ children, className, error, explanation }: Omit<Props, "hiddenLabel" | "label">) {
  return (
    <div className={classNames("flex flex-col w-full space-y-1.5", error && "group has-error", className)}>
      {children}
      {typeof explanation !== "undefined" && !error && (
        <p className="underlined-links text-sm text-gray-400 dark:text-gray-500">
          {typeof explanation === "string" ? <Trans components={{ code: <Code /> }}>{explanation}</Trans> : explanation}
        </p>
      )}
      {error && (
        <p className="text-sm text-red-500" data-role="error_message">
          {error}
        </p>
      )}
    </div>
  );
}

export function scrollErrorIntoView(scope: Document | HTMLElement = document) {
  const error = scope.querySelector("[data-role=error_message]");
  if (error) error.scrollIntoView({ behavior: "smooth", block: "center" });
}
